/**
 * @file Persistance : chargement (principal puis copie de secours), creation de partie, sauvegarde auto (10 s, mise en arriere-plan, evenements importants), export/import.
 */
import { CONFIG } from '@config/index';
import type { GameConfig } from '@config/index';
import { createNewPlayerData, deserializeSave } from '@core/index';
import type { PlayerData, Simulation, WallClock } from '@core/index';
import type { Locale } from '@shared/enums';
import { DEVICE_KEY, SAVE_BACKUP_KEY, SAVE_KEY } from '@platform/storage/StorageAdapter';
import type { StorageAdapter } from '@platform/storage/StorageAdapter';
import { randomId, randomSeed } from '@platform/time';

export const AUTOSAVE_SECONDS = 10;

export interface LoadOutcome {
  data: PlayerData;
  source: 'main' | 'backup' | 'new';
  /** Vrai si la sauvegarde principale etait illisible (toast d'avertissement). */
  recovered: boolean;
}

export class SaveManager {
  private timer: number | null = null;
  private dirty = false;
  private frozen = false;
  /** Appele apres chaque ecriture locale reussie (la synchro cloud s'y abonne). */
  onSaved: (() => void) | null = null;

  constructor(
    private readonly storage: StorageAdapter,
    private readonly clock: WallClock,
    private readonly config: GameConfig = CONFIG,
  ) {}

  /** Stockage sous-jacent (la synchro cloud y garde ses reperes). */
  get kv(): StorageAdapter {
    return this.storage;
  }

  async deviceId(): Promise<string> {
    const existing = await this.storage.load(DEVICE_KEY);
    if (existing) return existing;
    const id = randomId('d');
    await this.storage.save(DEVICE_KEY, id);
    return id;
  }

  private async freshDefaults(locale: Locale): Promise<PlayerData> {
    return createNewPlayerData(this.config, {
      seed: randomSeed(),
      nowMs: this.clock.nowMs(),
      playerId: randomId('p'),
      deviceId: await this.deviceId(),
      locale,
    });
  }

  /** Charge la sauvegarde principale, sinon la copie de secours, sinon cree une partie. */
  async loadOrCreate(locale: Locale): Promise<LoadOutcome> {
    const defaults = await this.freshDefaults(locale);
    const main = deserializeSave(await this.storage.load(SAVE_KEY), defaults);
    if (main.ok) return { data: main.data, source: 'main', recovered: false };
    const backup = deserializeSave(await this.storage.load(SAVE_BACKUP_KEY), defaults);
    if (backup.ok) return { data: backup.data, source: 'backup', recovered: main.reason !== 'empty' };
    return { data: defaults, source: 'new', recovered: main.reason !== 'empty' };
  }

  /** Sauvegarde locale brute (enveloppe JSON) ou null. */
  readRaw(): Promise<string | null> {
    return this.storage.load(SAVE_KEY);
  }

  /** Valide une sauvegarde venue du cloud ; retourne la partie correspondante (sans l'ecrire) ou null si elle est inutilisable. */
  async parseRemote(json: string, locale: Locale): Promise<LoadOutcome | null> {
    const r = deserializeSave(json, await this.freshDefaults(locale));
    return r.ok ? { data: r.data, source: 'main', recovered: false } : null;
  }

  /** Remplace la sauvegarde locale par une version du cloud (l'ancienne devient la copie de secours). `freeze` : fige les ecritures, la page va etre rechargee. */
  async replaceWithRemote(json: string, freeze = false): Promise<void> {
    if (freeze) this.frozen = true;
    const prev = await this.storage.load(SAVE_KEY);
    if (prev) await this.storage.save(SAVE_BACKUP_KEY, prev);
    await this.storage.save(SAVE_KEY, json);
  }

  /** Ecrit la sauvegarde ; la precedente version valide devient la copie de secours. */
  async save(sim: Simulation): Promise<void> {
    if (this.frozen) return;
    const prev = await this.storage.load(SAVE_KEY);
    if (prev) await this.storage.save(SAVE_BACKUP_KEY, prev);
    await this.storage.save(SAVE_KEY, sim.serialize(this.clock.nowMs()));
    this.dirty = false;
    this.onSaved?.();
  }

  /** Sauvegarde periodique + sur demande du core (SaveRequested). */
  startAutosave(sim: Simulation): () => void {
    const off = sim.bus.on('SaveRequested', () => {
      this.dirty = true;
      void this.save(sim);
    });
    this.timer = window.setInterval(() => void this.save(sim), AUTOSAVE_SECONDS * 1000);
    return () => {
      off();
      if (this.timer !== null) window.clearInterval(this.timer);
      this.timer = null;
    };
  }

  get hasPendingChanges(): boolean {
    return this.dirty;
  }

  exportString(sim: Simulation): string {
    return sim.serialize(this.clock.nowMs());
  }

  /** Valide puis remplace la sauvegarde ; l'app recharge la page ensuite (reconstruction propre de la Simulation). */
  async importString(json: string, locale: Locale): Promise<boolean> {
    const r = deserializeSave(json, await this.freshDefaults(locale), false);
    if (!r.ok) return false;
    await this.storage.save(SAVE_KEY, json);
    return true;
  }

  async wipe(): Promise<void> {
    await this.storage.remove(SAVE_KEY);
    await this.storage.remove(SAVE_BACKUP_KEY);
  }
}
